import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useToastStore } from "@/stores/toastStore";
import { env } from "@/config/env";
import { fetchDashboard, inviteUser, disableUser, enableUser, resendMcpInvite } from "./api";
import type { DashboardData } from "./api";
import { AttentionBanner } from "./AttentionBanner";
import { Pulse } from "./Pulse";
import { UserTable } from "./UserTable";
import { McpUserTable } from "./McpUserTable";

export function AdminPage() {
  const user = useAuthStore((s) => s.user);
  const { addToast } = useToastStore();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"ortvis" | "mcp">("ortvis");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteTier, setInviteTier] = useState("max");
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);

  const loadData = useCallback(() => {
    if (!user || !env.admin.emails.includes(user.email.toLowerCase())) return;

    setLoading(true);
    setError(null);
    fetchDashboard()
      .then(setData)
      .catch((err) => {
        setError((err as Error).message);
        addToast({ type: "error", message: "Failed to load dashboard" });
      })
      .finally(() => setLoading(false));
  }, [user, addToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Guard AFTER all hooks
  if (!user || !env.admin.emails.includes(user.email.toLowerCase())) {
    return <Navigate to="/" replace />;
  }

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const newUser = await inviteUser(inviteEmail.trim(), inviteTier, tab === "mcp");
      if (newUser.email_failed) {
        addToast({ type: "error", message: `Account created but invite email failed for ${newUser.email}` });
      } else {
        addToast({ type: "success", message: `Invite sent to ${newUser.email}` });
      }
      setInviteEmail("");
      setShowInvite(false);
      loadData();
    } catch (err) {
      addToast({ type: "error", message: (err as Error).message });
    } finally {
      setInviting(false);
    }
  };

  const handleDisable = async (email: string) => {
    try {
      await disableUser(email);
      addToast({ type: "success", message: `Disabled ${email}` });
      loadData();
    } catch (err) {
      addToast({ type: "error", message: (err as Error).message });
    }
  };

  const handleResend = async (email: string) => {
    try {
      await resendMcpInvite(email);
      addToast({ type: "success", message: `Invite sent again to ${email}` });
    } catch (err) {
      addToast({ type: "error", message: (err as Error).message });
    }
  };

  const handleEnable = async (email: string) => {
    try {
      await enableUser(email);
      addToast({ type: "success", message: `Enabled ${email}` });
      loadData();
    } catch (err) {
      addToast({ type: "error", message: (err as Error).message });
    }
  };

  return (
    <div className="cx-admin">
      <div className="cx-admin-header">
        <h1 className="cx-admin-title">Admin</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="cx-admin-btn" onClick={loadData}>
            Refresh
          </button>
          {!showInvite && (
            <button
              className="cx-admin-btn cx-admin-btn--primary"
              onClick={() => setShowInvite(true)}
            >
              {tab === "mcp" ? "Invite MCP user" : "Invite user"}
            </button>
          )}
        </div>
      </div>

      <div className="cx-admin-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === "ortvis"}
          className={`cx-admin-tab ${tab === "ortvis" ? "cx-admin-tab--active" : ""}`}
          onClick={() => {
            setTab("ortvis");
            setShowInvite(false);
          }}
        >
          Ortvis
        </button>
        <button
          role="tab"
          aria-selected={tab === "mcp"}
          className={`cx-admin-tab ${tab === "mcp" ? "cx-admin-tab--active" : ""}`}
          onClick={() => {
            setTab("mcp");
            setShowInvite(false);
          }}
        >
          Combify MCP
        </button>
      </div>

      {showInvite && (
        <div className="cx-admin-invite">
          <input
            type="email"
            className="cx-admin-input"
            placeholder="email@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleInvite()}
            autoFocus
          />
          <select
            className="cx-admin-input"
            style={{ width: "auto" }}
            value={inviteTier}
            onChange={(e) => setInviteTier(e.target.value)}
            aria-label="Tier"
          >
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="max">Max</option>
          </select>
          <button
            className="cx-admin-btn cx-admin-btn--primary"
            onClick={handleInvite}
            disabled={inviting || !inviteEmail.trim()}
          >
            {inviting ? "Sending..." : "Send invite"}
          </button>
          <button
            className="cx-admin-btn"
            onClick={() => {
              setShowInvite(false);
              setInviteEmail("");
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {loading && (
        <p className="cx-text-secondary cx-admin-empty">Loading dashboard...</p>
      )}

      {error && !loading && (
        <p className="cx-text-secondary cx-admin-empty">Failed to load dashboard</p>
      )}

      {!loading && !error && data && tab === "ortvis" && (
        <>
          <AttentionBanner attention={data.attention} opportunities={data.opportunities} />
          <Pulse pulse={data.pulse} />
          <UserTable
            users={data.users.filter((u) => !u.mcp)}
            onDisable={handleDisable}
            onEnable={handleEnable}
            adminEmail={user.email}
          />
        </>
      )}

      {!loading && !error && data && tab === "mcp" && (
        <>
          <div className="cx-admin-pulse">
            <span>
              <strong>
                {data.users.filter((u) => u.mcp).reduce((n, u) => n + u.mcp_today, 0)}
              </strong>{" "}
              calls today
            </span>
            <span>
              <strong>{data.users.filter((u) => u.mcp && u.status !== "disabled").length}</strong>{" "}
              active accounts
            </span>
          </div>
          <McpUserTable
            users={data.users.filter((u) => u.mcp)}
            onDisable={handleDisable}
            onEnable={handleEnable}
            onResend={handleResend}
          />
        </>
      )}
    </div>
  );
}
